import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { APP_TYPES } from "@lhd/components/CloneApp/constants";
import { useSlicerFilterState } from "@lhd/components/SearchAreaBox/hooks/useSlicerFilterState";
import { FilterSelected } from "@lhd/components/SearchAreaBox/types";
import { ROUTES } from "@lhd/constants";
import { useGetAuth, useGetPermissionsEachPage } from "@lhd/hooks";
import { IPermissionItem } from "types/appV2.type";
import {
  COMPANY_POLICIES_CATEGORY_OPTIONS,
  COMPANY_POLICIES_STATUS_OPTIONS,
} from "./utility";

const appID = "249"; // real = 249

const useList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const permissionsEachPage = useGetPermissionsEachPage();
  const { auth, isAdmin, isSuperAdmin, isSupperAdminLoggingIn } = useGetAuth();

  // Check if current user has permission to manage category/status catalog
  const canEditCatalog = Boolean(
    isAdmin ||
      isSuperAdmin ||
      isSupperAdminLoggingIn ||
      auth?.is_admin ||
      auth?.is_super_admin,
  );

  // Persist slicer filter selections in sessionStorage
  const [statusFilterSelected, setStatusFilterSelected] = useSlicerFilterState<
    FilterSelected[]
  >("status", []);
  const [categoryFilterSelected, setCategoryFilterSelected] =
    useSlicerFilterState<FilterSelected[]>("category", []);

  // Signal to close QuickView when filter changes
  const [closeSignal, setCloseSignal] = useState(0);

  const handleChangeStatus = (_val: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", "1");
    setSearchParams(next, { replace: true });
    setCloseSignal((prev) => prev + 1);
  };

  const handleChangeCategory = (_val: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("page", "1");
    setSearchParams(next, { replace: true });
    setCloseSignal((prev) => prev + 1);
  };

  // Check if all visible status options are checked
  const visibleStatus = statusFilterSelected.filter((x) => !x.is_hidden);
  const checkedStatus = visibleStatus.filter(
    (x) => x.status === "CHECKED" || x.status === "SELECTED",
  );
  const isStatusCheckAll =
    visibleStatus.length > 0 && checkedStatus.length === visibleStatus.length;

  // Check if all visible category options are checked
  const visibleCategory = categoryFilterSelected.filter((x) => !x.is_hidden);
  const checkedCategory = visibleCategory.filter(
    (x) => x.status === "CHECKED" || x.status === "SELECTED",
  );
  const isCategoryCheckAll =
    visibleCategory.length > 0 &&
    checkedCategory.length === visibleCategory.length;

  // If check all, omit filter_status / filter_category from payload
  const additionalFilter = {
    ...(statusFilterSelected.length === 0 || isStatusCheckAll
      ? {}
      : {
          filter_status: checkedStatus.map((x) => x.value),
        }),
    ...(categoryFilterSelected.length === 0 || isCategoryCheckAll
      ? {}
      : {
          filter_category: checkedCategory.map((x) => x.value),
        }),
  };

  return {
    appID,
    appType: APP_TYPES.COMPANY_POLICIES,
    url: ROUTES.COMPANY_POLICIES,
    permission: permissionsEachPage[ROUTES.COMPANY_POLICIES] as IPermissionItem,
    hasQuickView: true,
    statusFilterSelected,
    setStatusFilterSelected,
    categoryFilterSelected,
    setCategoryFilterSelected,
    statusKeys: ["ステータス", "status"],
    categoryKeys: ["カテゴリー", "category"],
    handleChangeStatus,
    handleChangeCategory,
    closeSignal,
    setCloseSignal,
    canEditCatalog,
    statusOptions: COMPANY_POLICIES_STATUS_OPTIONS,
    categoryOptions: COMPANY_POLICIES_CATEGORY_OPTIONS,
    additionalFilter,
    configShowBtnsOnHeader: {
      showDelete: true,
      showShare: true,
      showAddNew: true,
      typeShare: "multiple",
      showUndoRedoButtons: true,
      showViewModeSwitch: true,
      hideAddNewRecordButton: true,
      showImportExcelCsv: false,
      showExportCsv: false,
      showExportExcel: false,
    },
    configActionBtnsOnRow: {
      reorder: true,
    },
    showColumnAction: true,
  };
};

export type Props = ReturnType<typeof useList>;

export default useList;
